import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type {
  AppointmentInput,
  AppointmentType,
  ReminderType,
  RecurrencePattern,
  RecurrenceEndMode,
} from '../types';
import { RECURRENCE_LIMITS } from '../types';

// Helper function to format date as YYYY-MM-DD using local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: AppointmentInput) => Promise<boolean>;
  initialDate?: Date;
}

const APPOINTMENT_TYPES: AppointmentType[] = ['doctor', 'checkup', 'ultrasound', 'course', 'personal'];
const REMINDER_OPTIONS: ReminderType[] = ['none', '1_day', '1_week'];
const PATTERNS: RecurrencePattern[] = ['weekly', 'biweekly', 'monthly'];

export function AddAppointmentModal({
  isOpen,
  onClose,
  onSave,
  initialDate,
}: AddAppointmentModalProps) {
  const { t } = useTranslation('calendar');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(
    initialDate ? formatLocalDate(initialDate) : formatLocalDate(new Date())
  );
  const [time, setTime] = useState('10:00');
  const [isAllDay, setIsAllDay] = useState(false);
  const [appointmentType, setAppointmentType] = useState<AppointmentType>('personal');
  const [reminder, setReminder] = useState<ReminderType>('none');
  const [isSaving, setIsSaving] = useState(false);

  // Recurrence state
  const [isRecurring, setIsRecurring] = useState(false);
  const [pattern, setPattern] = useState<RecurrencePattern>('weekly');
  const [endMode, setEndMode] = useState<RecurrenceEndMode>('count');
  const [endDate, setEndDate] = useState('');
  const [count, setCount] = useState<number>(4);
  const [recurrenceError, setRecurrenceError] = useState<string | null>(null);

  // Update date when modal opens with a new initialDate
  useEffect(() => {
    if (isOpen && initialDate) {
      setDate(formatLocalDate(initialDate));
    }
  }, [isOpen, initialDate]);

  const patternCap = useMemo(() => RECURRENCE_LIMITS[pattern], [pattern]);

  if (!isOpen) return null;

  const validateRecurrence = (): string | null => {
    if (!isRecurring) return null;
    const start = new Date(date);
    if (endMode === 'until') {
      if (!endDate) return t('recurring.errors.endBeforeStart');
      const end = new Date(endDate);
      if (end < start) return t('recurring.errors.endBeforeStart');
      const maxEnd = new Date(start);
      maxEnd.setFullYear(maxEnd.getFullYear() + 1);
      if (end > maxEnd) return t('recurring.errors.maxOneYear');
    } else {
      if (!Number.isFinite(count) || count < 1 || count > patternCap) {
        return t('recurring.errors.countRange', { max: patternCap });
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!title.trim() || !date) return;

    const recError = validateRecurrence();
    if (recError) {
      setRecurrenceError(recError);
      return;
    }
    setRecurrenceError(null);

    setIsSaving(true);
    const success = await onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      appointment_date: date,
      appointment_time: isAllDay ? undefined : time,
      is_all_day: isAllDay,
      appointment_type: appointmentType,
      reminder,
      recurrence: isRecurring
        ? {
            pattern,
            endMode,
            endDate: endMode === 'until' ? endDate : undefined,
            count: endMode === 'count' ? count : undefined,
          }
        : undefined,
    });

    setIsSaving(false);
    if (success) {
      setTitle('');
      setDescription('');
      setTime('10:00');
      setIsAllDay(false);
      setAppointmentType('personal');
      setReminder('none');
      setIsRecurring(false);
      setPattern('weekly');
      setEndMode('count');
      setEndDate('');
      setCount(4);
      setRecurrenceError(null);
      onClose();
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('addModal.title')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form - scrollable */}
          <div className="bottom-sheet-content space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('addModal.titleLabel')}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('addModal.titlePlaceholder')}
              autoFocus
              className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('addModal.dateLabel')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
            />
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isAllDay"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
              className="w-5 h-5 rounded border-white/25 bg-white/10 text-pp-orange"
            />
            <label htmlFor="isAllDay" className="text-sm text-white/70">
              {t('addModal.allDay')}
            </label>
          </div>

          {/* Time (only if not all day) */}
          {!isAllDay && (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('addModal.timeLabel')}
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
              />
            </div>
          )}

          {/* Appointment Type */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('addModal.typeLabel')}
            </label>
            <div className="flex flex-wrap gap-2">
              {APPOINTMENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAppointmentType(type)}
                  className={`px-3 py-1.5 rounded-[5px] text-sm border transition-colors ${
                    appointmentType === type
                      ? 'bg-pp-teal text-white border-white/25'
                      : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                  }`}
                >
                  {t(`appointmentTypes.${type}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Reminder */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('addModal.reminderLabel')}
            </label>
            <select
              value={reminder}
              onChange={(e) => setReminder(e.target.value as ReminderType)}
              className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
            >
              {REMINDER_OPTIONS.map((option) => (
                <option key={option} value={option} className="bg-pp-dark text-white">
                  {t(`reminderTypes.${option}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Recurring toggle */}
          <div className="flex items-center gap-3 pt-2 border-t border-white/10">
            <input
              type="checkbox"
              id="isRecurring"
              checked={isRecurring}
              onChange={(e) => {
                setIsRecurring(e.target.checked);
                setRecurrenceError(null);
              }}
              className="w-5 h-5 rounded border-white/25 bg-white/10 text-pp-orange"
            />
            <label htmlFor="isRecurring" className="text-sm text-white/70">
              {t('addModal.recurring.toggle')}
            </label>
          </div>

          {isRecurring && (
            <>
              {/* Pattern */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('recurring.patternLabel')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {PATTERNS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPattern(p)}
                      className={`px-3 py-1.5 rounded-[5px] text-sm border transition-colors ${
                        pattern === p
                          ? 'bg-pp-teal text-white border-white/25'
                          : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                      }`}
                    >
                      {t(`recurring.patterns.${p}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* End mode */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('recurring.endModeLabel')}
                </label>
                <div className="flex gap-2">
                  {(['count', 'until'] as RecurrenceEndMode[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setEndMode(m)}
                      className={`px-3 py-1.5 rounded-[5px] text-sm border transition-colors ${
                        endMode === m
                          ? 'bg-pp-teal text-white border-white/25'
                          : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                      }`}
                    >
                      {t(`recurring.endMode.${m}`)}
                    </button>
                  ))}
                </div>
              </div>

              {endMode === 'until' ? (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('recurring.endDateLabel')}
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('recurring.countLabel')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={patternCap}
                    value={count}
                    onChange={(e) => setCount(parseInt(e.target.value || '0', 10))}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>
              )}

              {recurrenceError && (
                <p className="text-red-400 text-xs">{recurrenceError}</p>
              )}
            </>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('addModal.notesLabel')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('addModal.notesPlaceholder')}
              rows={3}
              className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
            />
          </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
              onClick={onClose}
            >
              {t('addModal.cancel')}
            </button>
            <button
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90"
              onClick={handleSave}
              disabled={!title.trim() || !date || isSaving}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('addModal.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
